import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import adminApi from '../../shared/api/AxiosAdmin';
import ChoshiiLogo from '../ChoshiiLogo'; 
import SmartVideoPlayerGallery from '../pages/Presentation/SmartVideoPlayerGallery';

type Targeting = {
  isWorldwide?: boolean;
  continents?: string[];
  countries?: string[];
  cities?: string[];
  hashtags?: string[];
};

type Stats = {
  impressionsTotal?: number;
  clicksTotal?: number;
  spendTotal?: number;
};

type PendingAd = {
  _id: string;
  name: string;
  status: 'draft' | 'running' | 'paused' | 'finished';
  reviewStatus: 'pending' | 'approved' | 'rejected';
  clickUrl: string;
  ctaText: string;
  createdAt: string;

  cpm?: number;
  dailyBudget?: number;
  totalBudget?: number;
  deliveryMode?: 'light' | 'balanced' | 'frequent';
  targeting?: Targeting;
  stats?: Stats;

  advertiser?: {
    _id: string;
    email?: string;
    profilename?: string;
  };
  media?: {
  _id: string;
  media: {
    url?: string;
    kind?: 'video' | 'image';
    isHls?: boolean;
    videoId?: string;
  }[];
  location?: { city?: string; country?: string };
};

};

const isVideoMedia = (m: { url?: string; isHls?: boolean }) => {
  if (m.isHls) return true;
  if (!m.url) return false;
  return (
    /\.(mp4|webm|mov|m4v)$/i.test(m.url) ||
    /\.m3u8(\?|$)/i.test(m.url) ||
    /\/hls\/playlist\//i.test(m.url)
  );
};

const AdminAdsReview: React.FC = () => {
  const [ads, setAds] = useState<PendingAd[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const loadAds = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.get<PendingAd[]>('/ads/admin/pending');
      setAds(res.data || []);
    } catch (err: any) {
      console.error('[AdminAdsReview] load failed', err);
      setError('Failed to load pending ads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAds();
  }, []);

  const handleApprove = async (id: string) => {
    try {
      await adminApi.post(`/ads/${id}/approve`, null);
      setAds((prev) => prev.filter((a) => a._id !== id));
    } catch (err) {
      console.error('Approve failed', err);
      alert('Approve failed');
    }
  };

  const handleReject = async (id: string) => {
    try {
      await adminApi.post(`/ads/${id}/reject`, null);
      setAds((prev) => prev.filter((a) => a._id !== id));
    } catch (err) {
      console.error('Reject failed', err);
      alert('Reject failed');
    }
  };

  const getLocationSummary = (ad: PendingAd): string => {
    const t = ad.targeting;
    if (!t) return 'Not specified';
    if (t.isWorldwide) return 'Worldwide';
    if (t.countries && t.countries.length) return t.countries.join(', ');
    if (t.continents && t.continents.length) return t.continents.join(', ');

    if (ad.media?.location?.country || ad.media?.location?.city) {
      const { city, country } = ad.media.location;
      return [city, country].filter(Boolean).join(', ');
    }
    return 'Not specified';
  };

  const getDeliveryLabel = (mode?: PendingAd['deliveryMode']) => {
    if (mode === 'light') return 'Light (show less often)';
    if (mode === 'frequent') return 'Frequent (show more often)';
    return 'Balanced';
  };

  const formatBudget = (v?: number) =>
    typeof v === 'number' ? `${v.toLocaleString()} €` : '—';

  // ----- Shell (StartPage/AdminLogin style) -----
  const shellStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    minHeight: '100vh',
    background: 'radial-gradient(circle at top, #020617 0, #000 40%, #020617 100%)',
    color: '#f9fafb',
    fontFamily: 'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
    display: 'flex',
    flexDirection: 'column',
  };

  const headerStyle: React.CSSProperties = {
    position: 'sticky',
    top: 0,
    zIndex: 30,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 25px',
    background: 'rgba(2, 6, 23, 0.9)',
    backdropFilter: 'blur(10px)',
    borderBottom: '1px solid rgba(148,163,184,0.2)',
  };

  const windowStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: 1100,
    borderRadius: 24,
    border: '1px solid rgba(148,163,184,0.3)',
    background: 'linear-gradient(145deg, rgba(15,23,42,0.98), rgba(2,6,23,0.98))',
    boxShadow: '0 25px 60px rgba(0,0,0,0.9)',
    padding: '20px 20px 24px',
    position: 'relative',
  };

  // ----- Loading view inside shell -----
  if (loading) {
    return (
      <div style={shellStyle}>
        <header style={headerStyle}>
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => navigate('/')}
          >
            <ChoshiiLogo
              animateSheen
              fontFamily="'Playfair Display', serif"
              style={{ width: 160 }}
              padding={0}
            />
          </div>

          <div
            style={{
              fontSize: 12,
              color: '#b1b8c7',
              border: '1px solid rgba(148,163,184,0.25)',
              padding: '6px 12px',
              borderRadius: 999,
              background: 'rgba(2,6,23,0.6)',
            }}
          >
            Ad Moderation
          </div>
        </header>

        <main style={{ flex: 1, display: 'grid', placeItems: 'center', padding: 24 }}>
          <div style={{ color: '#b1b8c7' }}>Loading pending ads…</div>
        </main>
      </div>
    );
  }

  return (
    <div style={shellStyle}>
      {/* Top bar */}
      <header style={headerStyle}>
        <div
          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
          title="Back to home"
        >
          <ChoshiiLogo
            animateSheen
            fontFamily="'Playfair Display', serif"
            style={{ width: 160 }}
            padding={0}
          />
        </div>

        <div
          style={{
            fontSize: 12,
            color: '#b1b8c7',
            border: '1px solid rgba(148,163,184,0.25)',
            padding: '6px 12px',
            borderRadius: 999,
            background: 'rgba(2,6,23,0.6)',
          }}
        >
          Ad Moderation
        </div>
      </header>

      {/* Center window */}
      <main
        style={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          padding: 24,
          overflow: 'auto',
        }}
        className="scroll-y"
      >
        <div style={windowStyle}>
          {/* Back arrow to /admin */}
          <button
            type="button"
            onClick={() => navigate('/admin')}
            aria-label="Back to Admin"
            title="Back to Admin"
            style={{
              position: 'absolute',
              top: 16,
              left: 16,
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg
              width="25"
              height="25"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#b1b8c7"
              strokeWidth="2.0"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          {/* Title */}
          <div style={{ textAlign: 'center', marginBottom: 18, marginTop: 12 }}>
            <h1
              style={{
                fontSize: 24,
                marginBottom: 6,
                letterSpacing: -0.03,
                color: '#b1b8c7',
              }}
            >
              Ad Moderation
            </h1>
            <p style={{ fontSize: 13, color: '#b1b8c7' }}>
              Review pending ads before they run.
            </p>
          </div>

          {error && (
            <div
              style={{
                marginBottom: 12,
                padding: 10,
                borderRadius: 10,
                color: '#fed7aa',
                background: 'rgba(127,29,29,0.3)',
                border: '1px solid rgba(248,113,113,0.6)',
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}

          {ads.length === 0 ? (
            <div style={{ fontSize: 13, color: '#b1b8c7', textAlign: 'center' }}>
              No pending ads right now 🎉
            </div>
          ) : (
            <div className="space-y-5">
              {ads.map((ad) => {
                const trimmedUrl = (ad.clickUrl || '').trim();
                const hasValidUrl = /^https?:\/\//i.test(trimmedUrl);

                return (
                  <div
                    key={ad._id}
                    className="bg-neutral-950/90 border border-sky-700/40 rounded-2xl p-4 md:p-5 shadow-[0_20px_60px_rgba(0,0,0,0.9)]"
                  >
                    {/* Header */}
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="px-2 py-0.5 text-[10px] rounded-full bg-sky-500/20 border border-sky-500/40 uppercase tracking-wide text-sky-200/90">
                            Pending review
                          </div>
                          <span className="text-[11px] text-sky-300/70 font-mono">
                            Status: {ad.status}
                          </span>
                        </div>

                        <h2 className="mt-2 text-lg font-semibold text-sky-50">
                          {ad.name}
                        </h2>

                        <div className="text-xs text-sky-300/70 mt-1">
                          Created:&nbsp;{new Date(ad.createdAt).toLocaleString()}
                        </div>

                        <div className="text-xs text-sky-300/80 mt-1">
                          Advertiser:{' '}
                          <span className="font-medium">
                            {ad.advertiser?.profilename || ad.advertiser?.email || 'Unknown'}
                          </span>
                        </div>
                      </div>

                      {/* CTA preview */}
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-[11px] text-sky-300/80">
                          CTA preview
                        </span>

                        {hasValidUrl ? (
                          <a
                            href={trimmedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-400 text-black text-sm font-semibold shadow-lg hover:bg-emerald-300 transition-colors"
                          >
                            {ad.ctaText || 'Visit website'}
                            <span className="text-xs">↗</span>
                          </a>
                        ) : (
                          <button
                            type="button"
                            disabled
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-neutral-700 text-neutral-300 text-sm font-semibold opacity-60 cursor-not-allowed"
                            title="Invalid or missing URL"
                          >
                            {ad.ctaText || 'Visit website'}
                          </button>
                        )}

                        <div className="max-w-xs text-[11px] text-sky-300/80 break-all">
                          {hasValidUrl ? trimmedUrl : 'No valid URL'}
                        </div>
                      </div>
                    </div>

                    {/* Media + details */}
                    <div className="flex flex-col gap-4 md:flex-row">
                      {/* Media bucket (kept as you already like it) */}
                      <div className="md:w-[100px] lg:w-[100px]">
                        <div className="text-xs text-sky-300/70 mb-1">
                          Creative preview
                        </div>
                        <div className="rounded-xl bg-neutral-900/90 border border-sky-700/40 p-2">
                          {ad.media?.media && ad.media.media.length > 0 ? (
<div className="flex gap-2 overflow-x-auto max-h-[64px] scrollbar-thin scrollbar-thumb-neutral-700">
 {ad.media.media.map((m, idx) =>
  m.url ? (
    isVideoMedia(m) ? (
<div
  key={idx}
  className="w-14 h-14 aspect-square rounded-md overflow-hidden flex-shrink-0 bg-black border border-sky-700/40"
>

  <SmartVideoPlayerGallery
    videoUrl={m.url}
    muted
    showControls={false}
    clickToToggle={false}
    preload="metadata"
    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
  />
</div>

    ) : (
 <img
  key={idx}
  src={m.url}
  className="w-14 h-14 aspect-square object-cover rounded-md flex-shrink-0 border border-sky-700/40 opacity-90"
/>


    )
  ) : null
)}


                            </div>
                          ) : (
                            <div className="w-full h-40 flex items-center justify-center text-xs text-neutral-500">
                              No preview
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Summary */}
                      <div className="flex-1 flex flex-col gap-3 text-sm">
                        <div className="rounded-xl bg-neutral-900/80 border border-sky-700/40 p-3">
                          <div className="text-xs uppercase tracking-wide text-sky-300/70 mb-2">
                            Campaign summary
                          </div>

                          <dl className="space-y-1.5 text-xs sm:text-sm">
                            <div className="flex justify-between gap-3">
                              <dt className="text-sky-300/80">Location</dt>
                              <dd className="text-sky-50 text-right">
                                {getLocationSummary(ad)}
                              </dd>
                            </div>

                            <div className="flex justify-between gap-3">
                              <dt className="text-sky-300/80">Daily budget</dt>
                              <dd className="text-sky-50 text-right">
                                {formatBudget(ad.dailyBudget)}
                              </dd>
                            </div>

                            <div className="flex justify-between gap-3">
                              <dt className="text-sky-300/80">Total budget</dt>
                              <dd className="text-sky-50 text-right">
                                {ad.totalBudget && ad.totalBudget > 0
                                  ? formatBudget(ad.totalBudget)
                                  : 'Unlimited'}
                              </dd>
                            </div>

                            <div className="flex justify-between gap-3">
                              <dt className="text-sky-300/80">CPM</dt>
                              <dd className="text-sky-50 text-right">
                                {typeof ad.cpm === 'number'
                                  ? `${ad.cpm.toFixed(2)} € / 1000 impressions`
                                  : 'Default'}
                              </dd>
                            </div>

                            <div className="flex justify-between gap-3">
                              <dt className="text-sky-300/80">Delivery</dt>
                              <dd className="text-sky-50 text-right">
                                {getDeliveryLabel(ad.deliveryMode)}
                              </dd>
                            </div>

                            {ad.stats && (
                              <div className="pt-1 mt-1 border-t border-sky-900/60 text-[11px] text-sky-300/80 flex justify-between gap-3">
                                <span>Impressions: {ad.stats.impressionsTotal ?? 0}</span>
                                <span>Clicks: {ad.stats.clicksTotal ?? 0}</span>
                                <span>
                                  Spend:{' '}
                                  {typeof ad.stats.spendTotal === 'number'
                                    ? `${ad.stats.spendTotal.toFixed(2)} €`
                                    : '0.00 €'}
                                </span>
                              </div>
                            )}
                          </dl>
                        </div>

                        {ad.media?.location && (
                          <div className="text-xs text-sky-300/80">
                            Original post location:&nbsp;
                            {[ad.media.location.city, ad.media.location.country]
                              .filter(Boolean)
                              .join(', ')}
                          </div>
                        )}

                        {/* Actions */}
                        <div className="mt-1 flex flex-wrap gap-3">
                          <button
                            onClick={() => handleApprove(ad._id)}
                            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-sky-500 text-black text-sm font-semibold shadow-lg hover:bg-sky-400 transition-colors"
                          >
                            ✅ Approve &amp; run
                          </button>
                          <button
                            onClick={() => handleReject(ad._id)}
                            className="px-4 py-2 rounded-full border border-red-500/70 text-red-200 text-xs font-semibold hover:bg-red-500/10 transition-colors"
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div
            style={{
              marginTop: 14,
              height: 1,
              width: '100%',
              background: 'linear-gradient(to right, transparent, #1f2937, transparent)',
            }}
          />

          <p style={{ marginTop: 12, textAlign: 'center', fontSize: 11, color: '#6b7280' }}>
            © {new Date().getFullYear()} • Admin access only
          </p>
        </div>
      </main>
    </div>
  );
};

export default AdminAdsReview;
